import { localized, msg, str } from '@lit/localize';
import hljs from 'highlight.js';
import type { PropertyValues } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';

import type {
  Id,
  WorkflowAuditDetail,
  WorkflowAuditSummary,
  WorkflowAuditTransition,
} from '../../api/types';
import { auditService } from '../../services/audit-service';
import { WebsocketService } from '../../services/websocket-service';
import { workflowService } from '../../services/workflow-service';
import { sharedUiStyles } from '../../styles/shared-ui.styles';
import {
  formatCurrencyCents,
  formatDateTime,
  formatDuration,
} from '../../utils/format';
import { sanitizeForDisplay } from '../workflow/utils/binary-display';
import { getLoopIterationDisplay } from '../workflow/utils/loop-iteration';

type AuditFilterDetail = {
  workflowId?: number | null;
  workflowName?: string | null;
  auditId?: number | null;
  errorsOnly?: boolean;
};

@customElement('kv-module-audit')
@localized()
export class KvModuleAudit extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      display: block;
      padding: 28px;
      color: var(--color-app-text, #1f1f1f);
      background:
        linear-gradient(
          180deg,
          var(--color-sidebar-bg, #f8fafc) 0%,
          var(--color-app-bg, #f8fafc) 100%
        );
      min-height: 100%;
      height: 100%;
      overflow: auto;
      box-sizing: border-box;
      --audit-surface: var(--kv-surface);
      --audit-surface-muted: var(--kv-surface-muted);
      --audit-surface-hover: var(--kv-surface-hover);
      --audit-border: var(--kv-border);
      --audit-accent: var(--kv-accent);
      --audit-accent-bg: var(--kv-accent-bg);
      --audit-shadow: var(--kv-shadow);
      --audit-warning-bg: var(--kv-warning-bg);
      --audit-warning-text: var(--kv-warning-text);
      --audit-text-muted: var(--color-app-text-secondary, #6b7280);
    }

    :host-context(sp-theme[color='dark']) {
      --audit-shadow: none;
    }

    .audit-shell {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin: 0 auto;
      max-width: 1280px;
      min-width: 0;
      width: min(100%, 1280px);
    }

    .audit-header {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-width: 760px;
    }

    h1 {
      color: var(--color-app-text, #1f1f1f);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
    }

    .audit-description {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
    }

    .filter-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
      border-radius: var(--kv-radius-panel);
      border: 1px solid var(--kv-accent-border-subtle);
      background: var(--audit-accent-bg);
      color: var(--color-app-text, #1f1f1f);
      font-size: 13px;
      min-width: 0;
    }

    .filter-label,
    .cell-main,
    .cell-muted,
    .transition-title,
    .transition-chip,
    .transition-meta-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .filter-label strong {
      font-weight: 700;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      margin-left: auto;
    }

    .pagination-label {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      font-weight: 650;
    }

    .table-toolbar {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: space-between;
      padding: 14px 16px;
      border-bottom: 1px solid var(--audit-border);
    }

    .table-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 14px;
      font-weight: 700;
      margin: 0;
    }

    .table-subtitle {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      margin: 4px 0 0;
    }

    .table-wrap {
      background: var(--audit-surface);
      border: 1px solid var(--audit-border);
      border-radius: var(--kv-radius-panel);
      box-shadow: var(--audit-shadow);
      overflow: hidden;
      sp-table-body {
        border-bottom: none;
      }
    }

    .table-scroll {
      overflow: auto;
    }

    :host([compact]) {
      background: transparent;
      box-sizing: border-box;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      padding: 0;
    }

    :host([compact]) .audit-shell {
      gap: 0;
      height: 100%;
      max-width: none;
      width: 100%;
    }

    :host([compact]) .table-wrap {
      background: transparent;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    :host([compact]) .table-scroll {
      flex: 1 1 auto;
      min-height: 0;
    }

    .audit-col-timestamp {
      box-sizing: border-box;
      flex: 0 0 220px;
      width: 220px;
    }

    .audit-col-automation {
      box-sizing: border-box;
      flex: 0 0 calc(100% - 850px);
      width: calc(100% - 850px);
    }

    .audit-col-trigger {
      box-sizing: border-box;
      flex: 0 0 130px;
      width: 130px;
    }

    .audit-col-status {
      box-sizing: border-box;
      flex: 0 0 140px;
      justify-content: center;
      text-align: center;
      width: 140px;
    }

    .audit-col-user {
      box-sizing: border-box;
      flex: 0 0 210px;
      width: 210px;
    }

    .audit-col-duration {
      box-sizing: border-box;
      flex: 0 0 150px;
      justify-content: center;
      text-align: center;
      width: 150px;
    }

    .audit-cell {
      min-width: 0;
      overflow: hidden;
    }

    .audit-cell .cell-main,
    .audit-cell .cell-muted {
      max-width: 100%;
    }

    sp-table.audit-table {
      --mod-table-border-width: 0px;
      --mod-table-cursor-row-default: default;
      --mod-table-edge-to-content: 20px;
      --mod-table-header-background-color: var(--audit-surface-muted);
      --mod-table-header-text-color: var(--audit-text-muted);
      --mod-table-row-background-color: transparent;
      --mod-table-row-background-color-hover: var(--audit-surface-hover);
      --mod-table-row-font-size: 13px;
      min-width: 860px;
      width: 100%;
    }

    sp-table-head {
      border-bottom: 1px solid var(--audit-border);
    }

    sp-table-head-cell {
      color: var(--audit-text-muted);
      font-size: 12px;
      font-weight: 700;
    }

    sp-table-cell {
      color: var(--color-app-text, #1f1f1f);
      font-size: 13px;
    }

    sp-table-row.audit-row {
      cursor: pointer;
      --mod-table-row-background-color-selected: var(--audit-accent-bg);
    }

    sp-table-row.audit-row sp-table-cell {
      align-items: center;
      display: flex;
    }

    sp-table-row.audit-row .audit-col-timestamp {
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
    }

    sp-table-row.audit-row.expanded {
      background: var(--audit-accent-bg);
    }

    .cell-main {
      display: block;
      color: var(--color-app-text, #1f1f1f);
      font-weight: 400;
    }

    .audit-col-automation .cell-main {
      font-weight: 650;
    }

    .cell-muted {
      display: block;
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      margin-top: 2px;
    }

    .status-chip {
      align-items: center;
      border-radius: var(--kv-radius-chip);
      display: inline-flex;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
      padding: 4px 9px;
      white-space: nowrap;
    }

    .status-chip.success {
      background: var(--color-success-bg, #efe);
      color: var(--color-success-text, #15803d);
    }

    .status-chip.failed {
      background: var(--color-error-bg, #fee);
      color: var(--color-error-text, #b91c1c);
    }

    .status-chip.running {
      background: var(--audit-accent-bg);
      color: var(--audit-accent);
    }

    .state-row {
      align-items: center;
      color: var(--color-app-text-secondary, #6b7280);
      display: flex;
      justify-content: center;
      min-height: 160px;
      text-align: center;
    }

    .empty-state {
      align-items: center;
      box-sizing: border-box;
      color: var(--color-app-text-secondary, #6b7280);
      display: flex;
      flex-direction: column;
      gap: 8px;
      justify-content: center;
      min-height: 220px;
      padding: 32px 24px;
      text-align: center;
      width: 100%;
    }

    .empty-state-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 15px;
      font-weight: 700;
      line-height: 1.3;
      margin: 0;
    }

    .empty-state-description {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
      max-width: 380px;
    }

    .error-panel {
      background: var(--color-error-bg, #fee);
      border: 1px solid rgba(220, 38, 38, 0.18);
      border-radius: 8px;
      color: var(--color-error-text, #b91c1c);
      font-size: 13px;
      line-height: 1.35;
      padding: 12px 14px;
    }

    .detail-grid {
      display: grid;
      gap: 12px;
      max-height: 60vh;
      overflow-y: auto;
      padding: 14px 0;
    }

    .detail-heading {
      align-items: center;
      display: flex;
      gap: 8px;
      justify-content: space-between;
      padding: 0 2px;
    }

    .detail-heading-main,
    .detail-actions {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      min-width: 0;
    }

    .detail-heading strong {
      color: var(--color-app-text, #1f1f1f);
      font-size: 13px;
    }

    .detail-row sp-table-cell {
      background: var(--audit-surface-muted);
    }

    .transition {
      border: 0;
      border-top: 1px solid var(--audit-border);
      border-radius: 0;
      background: transparent;
      overflow: visible;
    }

    .transition-header {
      align-items: center;
      background: transparent;
      border-bottom: 0;
      display: grid;
      gap: 8px;
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 12px 0 8px;
    }

    .transition-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 13px;
      font-weight: 700;
    }

    .transition-chips {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-end;
    }

    .transition-chip {
      background: var(--audit-surface);
      border: 1px solid var(--audit-border);
      border-radius: 999px;
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      font-weight: 650;
      max-width: 260px;
      padding: 3px 8px;
    }

    .transition-chip.error {
      background: var(--color-error-bg, #fee);
      border-color: transparent;
      color: var(--color-error-text, #b91c1c);
    }

    .transition-meta {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      padding: 0 0 12px;
    }

    .transition-pane {
      min-width: 0;
    }

    .transition-meta-label {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      font-weight: 700;
    }

    pre {
      margin: 6px 0 0;
      background: var(--audit-surface-muted);
      border: 1px solid var(--audit-border);
      border-radius: 8px;
      color: var(--color-app-text, #1f1f1f);
      cursor: text;
      font: 12px/1.5 var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      max-height: 220px;
      overflow: auto;
      padding: 10px;
      user-select: text;
      -webkit-user-select: text;
      white-space: pre-wrap;
      word-break: break-word;
    }

    pre * {
      cursor: text;
      user-select: text;
      -webkit-user-select: text;
    }

    pre code {
      background: transparent;
      cursor: text;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      padding: 0;
    }

    pre .hljs-attr {
      color: var(--color-syntax-attr);
    }

    pre .hljs-string {
      color: var(--color-syntax-string);
    }

    pre .hljs-number {
      color: var(--color-syntax-number);
    }

    pre .hljs-literal {
      color: var(--color-syntax-literal);
    }

    pre .hljs-punctuation {
      color: var(--color-syntax-punctuation);
    }

    .error-pane {
      background: var(--color-error-bg, #fee);
      border-color: rgba(220, 38, 38, 0.18);
      color: var(--color-error-text, #b91c1c);
    }

    .empty-pane {
      align-items: center;
      background: var(--audit-surface-muted);
      border: 1px dashed var(--audit-border);
      border-radius: 8px;
      color: var(--color-app-text-secondary, #6b7280);
      display: flex;
      font-size: 12px;
      justify-content: center;
      margin-top: 6px;
      min-height: 44px;
      padding: 10px;
    }

    .muted {
      color: var(--color-app-text-secondary, #6b7280);
    }

    sp-button[variant='secondary'] {
      --mod-button-background-color-default: var(--audit-accent-bg);
      --mod-button-background-color-hover: color-mix(
        in srgb,
        var(--audit-accent-bg) 86%,
        var(--audit-accent)
      );
      --mod-button-background-color-down: color-mix(
        in srgb,
        var(--audit-accent-bg) 76%,
        var(--audit-accent)
      );
      --mod-button-border-color-default: var(--kv-control-border);
      --mod-button-border-color-hover: var(--kv-accent-border);
      --mod-button-content-color-default: var(--audit-accent);
      --mod-button-content-color-hover: var(--audit-accent);
      --mod-button-content-color-down: var(--audit-accent);
      --mod-button-focus-ring-color: var(--audit-accent);
    }

    @media (max-width: 768px) {
      :host {
        padding: 16px 12px;
      }

      h1 {
        padding-left: var(--kv-mobile-title-inline-start);
        font-size: 22px;
      }

      .audit-description {
        padding-left: var(--kv-mobile-title-inline-start);
      }

      .transition-header {
        grid-template-columns: 1fr;
      }

      .transition-chips {
        justify-content: flex-start;
      }

      .transition-meta {
        grid-template-columns: 1fr;
      }
    }
    `,
  ];

  @property({ type: Boolean }) accessor compact = false;
  @property({ type: String }) accessor searchTerm = '';
  @state() accessor audits: WorkflowAuditSummary[] = [];
  @state() accessor expandedAuditId: number | null = null;
  @state() accessor detailsById = new Map<number, WorkflowAuditDetail>();
  @state() accessor loading = false;
  @state() accessor errorMessage: string | null = null;
  @state() accessor workflowFilterId: number | null = null;
  @state() accessor workflowFilterName: string | null = null;
  @state() accessor transitionErrorsOnly = false;
  @state() accessor currentPage = 1;
  @state() accessor hasNextPage = false;
  private requestedAuditId: number | null = null;
  private readonly pageSize = 100;
  private sockets = new Map<Id, WebsocketService>();
  private refreshTimeout?: ReturnType<typeof setTimeout>;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadFilterFromStorage();
    this.emitFilterState();
    void this.loadAudits();
    void this.syncWorkflowSockets();
    window.addEventListener('audit-filter-change', this.onAuditFilterChange);
  }

  disconnectedCallback(): void {
    window.removeEventListener('audit-filter-change', this.onAuditFilterChange);
    this.disconnectSockets();
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    if (!changedProperties.has('searchTerm')) return;
    if (changedProperties.get('searchTerm') === undefined && !this.searchTerm) {
      return;
    }
    this.currentPage = 1;
    this.hasNextPage = false;
    this.expandedAuditId = null;
    this.detailsById = new Map();
    this.scheduleAuditRefresh();
  }

  private async loadAudits(silent = false) {
    if (!silent) {
      this.loading = true;
    }
    this.errorMessage = null;
    try {
      const params =
        this.workflowFilterId != null
          ? {
              workflowId: this.workflowFilterId,
              search: this.searchTerm.trim() || undefined,
              page: this.currentPage,
              limit: this.pageSize,
            }
          : {
              search: this.searchTerm.trim() || undefined,
              page: this.currentPage,
              limit: this.pageSize,
            };
      const response = await auditService.list(undefined, params);
      this.audits = response.audits;
      this.hasNextPage = response.pagination.hasNextPage;
      this.emitSummaryState();
      await this.expandRequestedAudit();
      return true;
    } catch (error) {
      this.hasNextPage = false;
      this.errorMessage =
        error instanceof Error ? error.message : msg('Failed to load audits');
      return false;
    } finally {
      if (!silent) {
        this.loading = false;
      }
    }
  }

  private async toggleAudit(audit: WorkflowAuditSummary) {
    if (this.expandedAuditId === audit.id) {
      this.expandedAuditId = null;
      this.requestedAuditId = null;
      return;
    }
    this.expandedAuditId = audit.id;
    this.requestedAuditId = null;

    if (!this.detailsById.has(audit.id)) {
      await this.refreshAuditDetail(audit.id);
    }
  }

  private async expandRequestedAudit() {
    if (this.requestedAuditId == null) return;
    const audit = this.audits.find((item) => item.id === this.requestedAuditId);
    if (!audit) return;
    this.expandedAuditId = audit.id;
    if (!this.detailsById.has(audit.id)) {
      await this.refreshAuditDetail(audit.id);
    }
  }

  private onAuditFilterChange = (event: Event) => {
    const detail = (event as CustomEvent<AuditFilterDetail>).detail;
    if (!detail) return;
    this.applyWorkflowFilter(detail.workflowId ?? null, detail.workflowName, {
      auditId: detail.auditId ?? null,
      errorsOnly: detail.errorsOnly,
    });
  };

  private loadFilterFromStorage() {
    try {
      const raw = localStorage.getItem('kv.audit.filter');
      if (!raw) return;
      const parsed = JSON.parse(raw) as AuditFilterDetail;
      if (typeof parsed.workflowId === 'number') {
        this.applyWorkflowFilter(
          parsed.workflowId,
          parsed.workflowName ?? null,
          {
            auditId: parsed.auditId ?? null,
            errorsOnly: parsed.errorsOnly,
          }
        );
      }
    } catch (_) {
      /* ignore */
    }
  }

  private applyWorkflowFilter(
    workflowId: number | null,
    workflowName?: string | null,
    options: { auditId?: number | null; errorsOnly?: boolean } = {}
  ) {
    this.workflowFilterId = workflowId;
    this.workflowFilterName = workflowName ?? null;
    this.requestedAuditId =
      typeof options.auditId === 'number' ? options.auditId : null;
    this.transitionErrorsOnly = Boolean(options.errorsOnly);
    this.emitFilterState();
    this.currentPage = 1;
    this.hasNextPage = false;
    this.expandedAuditId = null;
    this.detailsById = new Map();
    void this.loadAudits();
  }

  setWorkflowFilter(
    workflowId: number | null,
    workflowName: string | null = null,
    options: { auditId?: number | null; errorsOnly?: boolean } = {}
  ) {
    if (
      this.workflowFilterId === workflowId &&
      this.workflowFilterName === workflowName &&
      this.requestedAuditId === (options.auditId ?? null) &&
      this.transitionErrorsOnly === Boolean(options.errorsOnly)
    ) {
      this.emitFilterState();
      void this.expandRequestedAudit();
      return;
    }
    if (workflowId == null) {
      this.clearWorkflowFilter();
      return;
    }
    try {
      localStorage.setItem(
        'kv.audit.filter',
        JSON.stringify({ workflowId, workflowName, ...options })
      );
    } catch (_) {
      /* ignore */
    }
    this.applyWorkflowFilter(workflowId, workflowName, options);
  }

  clearFilter() {
    this.clearWorkflowFilter();
  }

  private clearWorkflowFilter = () => {
    this.workflowFilterId = null;
    this.workflowFilterName = null;
    this.requestedAuditId = null;
    this.transitionErrorsOnly = false;
    this.emitFilterState();
    this.currentPage = 1;
    this.hasNextPage = false;
    this.expandedAuditId = null;
    this.detailsById = new Map();
    try {
      localStorage.removeItem('kv.audit.filter');
    } catch (_) {
      /* ignore */
    }
    void this.loadAudits();
  };

  private emitFilterState() {
    this.dispatchEvent(
      new CustomEvent('audit-filter-state-change', {
        detail: {
          workflowId: this.workflowFilterId,
          workflowName: this.workflowFilterName,
          auditId: this.requestedAuditId,
          errorsOnly: this.transitionErrorsOnly,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitSummaryState() {
    this.dispatchEvent(
      new CustomEvent('audit-summary-state-change', {
        detail: {
          count: this.audits.length,
          hasNextPage: this.hasNextPage,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async refreshAuditDetail(id: number) {
    try {
      const detail = await auditService.get(undefined, id);
      this.detailsById = new Map(this.detailsById).set(id, detail);
    } catch (error) {
      this.errorMessage =
        error instanceof Error
          ? error.message
          : msg('Failed to load audit details');
    }
  }

  private formatDate(value: string | null) {
    return formatDateTime(value, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  private formatAuditDuration(durationMs: number | null) {
    if (durationMs == null) return '';
    const formatted = formatDuration(durationMs);
    if (!formatted.includes(' ')) return formatted;
    return formatted
      .split(' ')
      .filter((part) => !part.endsWith('ms'))
      .join(' ');
  }

  private isAuditStatus(
    value: unknown
  ): value is WorkflowAuditSummary['status'] {
    return value === 'running' || value === 'success' || value === 'failed';
  }

  private formatUser(audit: WorkflowAuditSummary) {
    const user = audit.triggeredBy;
    if (!user) return '—';
    return user.name ?? msg('Unknown user');
  }

  private getStatusClass(status: WorkflowAuditSummary['status']) {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'failed';
      default:
        return 'running';
    }
  }

  private formatStatus(status: WorkflowAuditSummary['status']) {
    switch (status) {
      case 'success':
        return msg('Success');
      case 'failed':
        return msg('Failed');
      default:
        return msg('Running');
    }
  }

  private formatTrigger(trigger: WorkflowAuditSummary['trigger']) {
    return String(trigger)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private formatTransitionOutput(value?: Record<string, unknown>) {
    if (!value) return '—';
    return JSON.stringify(sanitizeForDisplay(value), null, 2);
  }

  private escapeHtml(value: string) {
    return value.replace(
      /[&<>"']/g,
      (char) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        })[char] ?? char
    );
  }

  private highlightJson(jsonString: string) {
    try {
      return hljs.highlight(jsonString, { language: 'json' }).value;
    } catch {
      return this.escapeHtml(jsonString);
    }
  }

  private formatTransitionCosts(value?: number) {
    return formatCurrencyCents(value);
  }

  private scheduleAuditRefresh() {
    if (this.refreshTimeout) return;
    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = undefined;
      void this.loadAudits(true);
    }, 200);
  }

  private async goToPage(page: number) {
    if (page < 1 || page === this.currentPage) return;
    const previousPage = this.currentPage;
    const previousExpandedAuditId = this.expandedAuditId;
    const previousDetailsById = this.detailsById;
    this.currentPage = page;
    this.expandedAuditId = null;
    this.detailsById = new Map();
    const loaded = await this.loadAudits();
    if (!loaded) {
      this.currentPage = previousPage;
      this.expandedAuditId = previousExpandedAuditId;
      this.detailsById = previousDetailsById;
    }
  }

  private handleAuditEvent(data: unknown) {
    if (!data || typeof data !== 'object') return;
    const payload = data as Record<string, unknown>;
    const workflowId = payload.workflowId;
    if (
      this.workflowFilterId != null &&
      typeof workflowId === 'number' &&
      workflowId !== this.workflowFilterId
    ) {
      return;
    }
    const type = payload.type;
    if (type === 'workflowAuditCreated') {
      this.scheduleAuditRefresh();
      return;
    }
    if (type === 'workflowAuditUpdated') {
      const auditId = payload.auditId;
      if (typeof auditId !== 'number') {
        return;
      }
      const index = this.audits.findIndex((audit) => audit.id === auditId);
      if (index === -1) {
        return;
      }
      const nextStatus = this.isAuditStatus(payload.status)
        ? payload.status
        : undefined;
      const next = [...this.audits];
      next[index] = {
        ...next[index],
        status: nextStatus ?? next[index].status,
        finishedAt:
          typeof payload.finishedAt === 'string'
            ? payload.finishedAt
            : next[index].finishedAt,
        durationMs:
          typeof payload.durationMs === 'number'
            ? payload.durationMs
            : next[index].durationMs,
      };
      this.audits = next;
      return;
    }
    if (type === 'taskAuditCreated') {
      const audit = payload.audit as { workflowAuditId?: number } | undefined;
      if (
        this.expandedAuditId &&
        audit?.workflowAuditId === this.expandedAuditId
      ) {
        void this.refreshAuditDetail(this.expandedAuditId);
      }
    }
  }

  private async syncWorkflowSockets() {
    try {
      const workflows = await workflowService.list();
      const workflowIds = new Set(
        workflows
          .map((workflow) => workflow.id)
          .filter((id): id is number => typeof id === 'number')
      );

      for (const id of workflowIds) {
        if (this.sockets.has(id)) continue;
        const socket = new WebsocketService();
        socket.connectToWorkflow(id, {
          onMessage: (data) => this.handleAuditEvent(data),
          onError: (error) =>
            console.error('🚨 WebSocket error for audit feed', id, error),
        });
        this.sockets.set(id, socket);
      }

      for (const [id, socket] of this.sockets.entries()) {
        if (workflowIds.has(id)) continue;
        socket.disconnect();
        this.sockets.delete(id);
      }
    } catch (error) {
      console.error('Failed to connect audit websocket feeds', error);
    }
  }

  private disconnectSockets() {
    for (const socket of this.sockets.values()) {
      socket.disconnect();
    }
    this.sockets.clear();
  }

  private renderStatusChip(status: WorkflowAuditSummary['status']) {
    return html`
      <span class="status-chip ${this.getStatusClass(status)}">
        ${this.formatStatus(status)}
      </span>
    `;
  }

  private renderTransitionPane(
    label: string,
    value: Record<string, unknown> | string | undefined,
    emptyText: string,
    error = false
  ) {
    const hasValue =
      typeof value === 'string'
        ? value.trim().length > 0
        : value !== undefined && value !== null;

    return html`
      <div class="transition-pane">
        <div class="transition-meta-label">${label}</div>
        ${
          hasValue
            ? typeof value === 'string'
              ? html`<pre class=${error ? 'error-pane' : ''}>${value}</pre>`
              : html`<pre class="hljs ${error ? 'error-pane' : ''}"><code>${unsafeHTML(
                  this.highlightJson(this.formatTransitionOutput(value))
                )}</code></pre>`
            : html`<div class="empty-pane">${emptyText}</div>`
        }
      </div>
    `;
  }

  private toggleTransitionErrorsOnly = (event: Event) => {
    event.stopPropagation();
    this.transitionErrorsOnly = !this.transitionErrorsOnly;
    this.emitFilterState();
  };

  private renderTransitions(transitions: WorkflowAuditTransition[]) {
    const visibleTransitions = this.transitionErrorsOnly
      ? transitions.filter((transition) => transition.error)
      : transitions;
    if (!visibleTransitions.length) {
      if (this.transitionErrorsOnly) {
        return html`<div class="state-row">${msg('No task errors recorded.')}</div>`;
      }
      return html`<div class="state-row">${msg('No task transitions recorded.')}</div>`;
    }

    return html`
      ${visibleTransitions.map((transition) => {
        const nextTasks = transition.nextTasks
          .map((task) => `${task.type} (#${task.id})`)
          .join(', ');
        const hasError = Boolean(transition.error);
        const loopIterationDisplay = getLoopIterationDisplay(
          transition.loopIteration
        );
        return html`
          <div class="transition">
            <div class="transition-header">
              <div class="transition-title">
                ${transition.taskType} (#${transition.taskId})
              </div>
              <div class="transition-chips">
                <span class="transition-chip">${msg('Next')}: ${nextTasks || msg('None')}</span>
                <span class="transition-chip">
                  ${this.formatDate(transition.timestamp ?? null)}
                </span>
                <span class="transition-chip">
                  ${this.formatTransitionCosts(transition.costs)}
                </span>
                ${
                  loopIterationDisplay
                    ? html`
                        <span class="transition-chip">
                          ${msg(
                            str`Iteration ${loopIterationDisplay.current} of ${loopIterationDisplay.total}`
                          )}
                        </span>
                      `
                    : nothing
                }
                ${
                  hasError
                    ? html`<span class="transition-chip error">${msg('Error')}</span>`
                    : nothing
                }
              </div>
            </div>
            <div class="transition-meta">
              ${this.renderTransitionPane(msg('Input'), transition.input, msg('No input'))}
              ${this.renderTransitionPane(msg('Output'), transition.output, msg('No output'))}
              ${this.renderTransitionPane(
                msg('Error'),
                transition.error,
                msg('No error'),
                hasError
              )}
            </div>
          </div>
        `;
      })}
    `;
  }

  private renderTableHeader(showUser: boolean) {
    return html`
      <sp-table-head>
        <sp-table-row>
          <sp-table-head-cell class="audit-cell audit-col-timestamp">
            ${msg('Timestamp')}
          </sp-table-head-cell>
          <sp-table-head-cell class="audit-cell audit-col-automation">
            ${msg('Automation')}
          </sp-table-head-cell>
          <sp-table-head-cell class="audit-cell audit-col-trigger">
            ${msg('Trigger')}
          </sp-table-head-cell>
          <sp-table-head-cell class="audit-cell audit-col-status">
            ${msg('Status')}
          </sp-table-head-cell>
          ${
            showUser
              ? html`<sp-table-head-cell class="audit-cell audit-col-user">
                  ${msg('User')}
                </sp-table-head-cell>`
              : nothing
          }
          <sp-table-head-cell class="audit-cell audit-col-duration">
            ${msg('Duration')}
          </sp-table-head-cell>
        </sp-table-row>
      </sp-table-head>
    `;
  }

  private renderEmptyTableRow(columnCount: number) {
    const hasSearch = this.searchTerm.trim().length > 0;
    const title = hasSearch
      ? msg('No history matches this search')
      : this.workflowFilterId != null
        ? msg('No history for this automation yet')
        : msg('No automation history yet');
    const description = hasSearch
      ? msg('Try a different search term to find older automation runs.')
      : this.workflowFilterId != null
        ? msg('Runs for this automation will appear here after it starts.')
        : msg('Every automation run will appear here once work has started.');

    return html`<sp-table-row>
      <sp-table-cell colspan=${columnCount}>
        <div class="empty-state">
          <h3 class="empty-state-title">${title}</h3>
          <p class="empty-state-description">${description}</p>
        </div>
      </sp-table-cell>
    </sp-table-row>`;
  }

  private renderAuditDetailRow(
    detail: WorkflowAuditDetail | undefined,
    visibleTransitionCount: number | undefined,
    errorTransitionCount: number,
    columnCount: number
  ) {
    return html`<sp-table-row class="detail-row">
      <sp-table-cell colspan=${columnCount}>
        <div class="detail-grid">
          <div class="detail-heading">
            <div class="detail-heading-main">
              <strong>${msg('Transition details')}</strong>
              ${
                detail
                  ? html`<span class="cell-muted">
                      ${
                        visibleTransitionCount === 1
                          ? msg('1 transition')
                          : msg(str`${visibleTransitionCount} transitions`)
                      }${
                        this.transitionErrorsOnly
                          ? ` ${msg('with errors')}`
                          : ''
                      }
                    </span>`
                  : nothing
              }
            </div>
            ${
              detail
                ? html`<div class="detail-actions">
                    <sp-button
                      size="s"
                      variant="secondary"
                      ?disabled=${errorTransitionCount === 0}
                      @click=${this.toggleTransitionErrorsOnly}
                    >
                      ${
                        this.transitionErrorsOnly
                          ? msg('Show all tasks')
                          : msg('Show errors only')
                      }
                    </sp-button>
                  </div>`
                : nothing
            }
          </div>
          ${
            detail
              ? this.renderTransitions(detail.transitions)
              : html`<div class="state-row">${msg('Loading transition details...')}</div>`
          }
        </div>
      </sp-table-cell>
    </sp-table-row>`;
  }

  private renderAuditRow(
    audit: WorkflowAuditSummary,
    showUser: boolean,
    columnCount: number
  ) {
    const detail = this.detailsById.get(audit.id);
    const isExpanded = this.expandedAuditId === audit.id;
    const errorTransitionCount =
      detail?.transitions.filter((transition) => transition.error).length ?? 0;
    const visibleTransitionCount =
      detail && this.transitionErrorsOnly
        ? errorTransitionCount
        : detail?.transitions.length;
    return html`
      <sp-table-row
        class=${isExpanded ? 'audit-row expanded' : 'audit-row'}
        @click=${() => this.toggleAudit(audit)}
      >
        <sp-table-cell class="audit-cell audit-col-timestamp">
          <span class="cell-main" title=${this.formatDate(audit.startedAt)}>
            ${this.formatDate(audit.startedAt)}
          </span>
          <span class="cell-muted" title=${msg(str`Run #${audit.id}`)}>
            ${msg(str`Run #${audit.id}`)}
          </span>
        </sp-table-cell>
        <sp-table-cell class="audit-cell audit-col-automation">
          <span class="cell-main" title=${audit.workflowName}>
            ${audit.workflowName}
          </span>
        </sp-table-cell>
        <sp-table-cell class="audit-cell audit-col-trigger">
          <span class="cell-main" title=${this.formatTrigger(audit.trigger)}>
            ${this.formatTrigger(audit.trigger)}
          </span>
        </sp-table-cell>
        <sp-table-cell class="audit-cell audit-col-status">
          ${this.renderStatusChip(audit.status)}
        </sp-table-cell>
        ${
          showUser
            ? html`<sp-table-cell class="audit-cell audit-col-user">
                <span class="cell-main" title=${this.formatUser(audit)}>
                  ${this.formatUser(audit)}
                </span>
              </sp-table-cell>`
            : nothing
        }
        <sp-table-cell class="audit-cell audit-col-duration">
          <span
            class="cell-main"
            title=${this.formatAuditDuration(audit.durationMs)}
          >
            ${this.formatAuditDuration(audit.durationMs)}
          </span>
        </sp-table-cell>
      </sp-table-row>
      ${
        isExpanded
          ? this.renderAuditDetailRow(
              detail,
              visibleTransitionCount,
              errorTransitionCount,
              columnCount
            )
          : nothing
      }
    `;
  }

  private renderTableBody(
    visibleAudits: WorkflowAuditSummary[],
    showUser: boolean,
    columnCount: number
  ) {
    return html`<sp-table-body>
      ${
        this.loading
          ? html`<sp-table-row>
              <sp-table-cell colspan=${columnCount}>
                <div class="state-row">${msg('Loading audit runs...')}</div>
              </sp-table-cell>
            </sp-table-row>`
          : visibleAudits.length === 0
            ? this.renderEmptyTableRow(columnCount)
            : visibleAudits.map((audit) =>
                this.renderAuditRow(audit, showUser, columnCount)
              )
      }
    </sp-table-body>`;
  }

  render() {
    const visibleAudits = this.audits;
    const showUser = visibleAudits.some((audit) => audit.triggeredBy?.id);
    const columnCount = showUser ? 6 : 5;
    const filterLabel =
      this.workflowFilterId != null
        ? (this.workflowFilterName ?? `Automation ${this.workflowFilterId}`)
        : null;
    const showPagination = this.hasNextPage || this.currentPage > 1;
    return html`
      <div class="audit-shell">
        ${
          this.compact
            ? nothing
            : html`<header class="audit-header">
                <h1>${msg('Audit Log')}</h1>
                <p class="audit-description">
                  ${msg('Review automation runs, execution status, and task transition history.')}
                </p>
              </header>`
        }
        ${
          filterLabel && !this.compact
            ? html`<div class="filter-bar">
                <div class="filter-label">
                  ${msg('Filtered by')} <strong>${filterLabel}</strong>
                </div>
                <sp-button
                  size="s"
                  variant="secondary"
                  @click=${this.clearWorkflowFilter}
                >
                  ${msg('Clear')}
                </sp-button>
              </div>`
            : nothing
        }
        ${
          this.errorMessage
            ? html`<div class="error-panel">${this.errorMessage}</div>`
            : nothing
        }
        <section class="table-wrap">
          ${
            this.compact && !showPagination
              ? nothing
              : html`<div class="table-toolbar">
            ${
              this.compact
                ? nothing
                : html`<div>
                    <h2 class="table-title">${msg('Automation runs')}</h2>
                    <p class="table-subtitle">
                      ${
                        this.loading
                          ? msg('Loading automation runs...')
                          : this.audits.length === 1
                            ? msg('1 run on this page')
                            : msg(str`${this.audits.length} runs on this page`)
                      }
                    </p>
                  </div>`
            }
            ${
              showPagination
                ? html`<div class="pagination">
                    <span class="pagination-label">
                      ${msg(str`Page ${this.currentPage}`)}
                    </span>
                    <sp-button
                      size="s"
                      variant="secondary"
                      ?disabled=${this.loading || this.currentPage <= 1}
                      @click=${() => this.goToPage(this.currentPage - 1)}
                    >
                      ${msg('Previous')}
                    </sp-button>
                    <sp-button
                      size="s"
                      variant="secondary"
                      ?disabled=${this.loading || !this.hasNextPage}
                      @click=${() => this.goToPage(this.currentPage + 1)}
                    >
                      ${msg('Next')}
                    </sp-button>
                  </div>`
                : nothing
            }
          </div>`
          }
          <div class="table-scroll">
            <sp-table class="audit-table" quiet>
              ${this.renderTableHeader(showUser)}
              ${this.renderTableBody(visibleAudits, showUser, columnCount)}
            </sp-table>
          </div>
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-audit': KvModuleAudit;
  }
}
